import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Card, CardBody, Col, Row, Alert } from 'reactstrap';
import PropTypes from 'prop-types';
import { Buffer } from 'buffer';
import Recaptcha from 'reaptcha';
import oauthService from '../../../services/oauthService';
import { SiteName } from '../../../shared/components/domainSupport';
import { jsonHeader } from '../../../config';
import LoadingSpinner from '../../../shared/components/widgets/LoadingSpinner';

/**
 * PlatformAuthForm - OAuth authorization form for ad platforms
 *
 * NOTE: "adPlatform" prop refers to AD PLATFORM (amazon, walmart, google, tiktok, etc.)
 * This is different from SYSTEM PLATFORM (limitless, adjuggernaut, adrev)
 *
 * @param {string} adPlatform - The ad platform to authorize (e.g., 'google', 'tiktok', 'amazon')
 * @param {string} apiType - The API type to authorize (e.g., 'ad-api', 'account-api')
 * @param {object} platformConfig - Configuration for the ad platform
 */
const PlatformAuthForm = ({ adPlatform, apiType = 'ad-api', platformConfig = null }) => {
  const user = useSelector(state => state.authentication.user);
  const sellerSelect = useSelector(state => state.sellerSelect);

  const [accountId, setAccountId] = useState(null);
  const [accountName, setAccountName] = useState(null);
  const [accountMetadata, setAccountMetadata] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [recaptchaResponse, setRecaptchaResponse] = useState('');

  const platformName = adPlatform.charAt(0).toUpperCase() + adPlatform.slice(1);
  const loggedIn = !!user?.jwtToken;

  // Extract accountId and metadata from URL params or Redux
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const accountIdParam = urlParams.get('accountId');
    const edataParam = urlParams.get('edata');

    if (accountIdParam) {
      setAccountId(accountIdParam);
      setAccountName(sellerSelect?.label || `Account ${accountIdParam}`);
      setAccountMetadata({
        accountId: accountIdParam,
        accountName: sellerSelect?.label || `Account ${accountIdParam}`,
        accountType: sellerSelect?.type || null,
      });
    } else if (edataParam) {
      // Decode base64 edata parameter
      try {
        const decoded = Buffer.from(decodeURIComponent(edataParam), 'base64').toString('utf-8');
        const data = JSON.parse(decoded);
        setAccountId(data.accountId);
        setAccountName(data.accountName || `Account ${data.accountId}`);
        setAccountMetadata(data);
      } catch (err) {
        setError('Invalid account data in URL');
      }
    } else if (loggedIn && sellerSelect?.value) {
      setAccountId(sellerSelect.value);
      setAccountName(sellerSelect.label || `Account ${sellerSelect.value}`);
      setAccountMetadata({
        accountId: sellerSelect.value,
        accountName: sellerSelect.label || `Account ${sellerSelect.value}`,
        accountType: sellerSelect.type || null,
      });
    } else {
      setError('No account selected. Please select an account or use a valid authorization link.');
    }
  }, [loggedIn, sellerSelect]);

  // Check for existing authorizations and show warnings
  useEffect(() => {
    if (!accountId) return;

    const checkPairing = async () => {
      const headerWithAuth = {
        ...jsonHeader,
        authorization: user?.jwtToken ? `JWT ${user.jwtToken}` : undefined,
      };

      const result = await oauthService.checkExistingAuthorizations({
        accountId,
        platform: adPlatform,
        options: { headers: headerWithAuth },
      });

      // Example: Warn if attaching TikTok account API without main API
      if (apiType === 'account-api' && !result.authorizations.includes('ad-api')) {
        setWarning(`This account does not have ${platformName} Ad API attached. It's recommended to attach the Ad API first.`);
      }
    };

    checkPairing();
  }, [accountId, adPlatform, apiType, user, platformName]);

  const handleAuthorize = async () => {
    if (!accountId) {
      setError('No account selected');
      return;
    }

    if (!loggedIn && !recaptchaResponse) {
      setError('Please complete the reCAPTCHA');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const headerWithAuth = {
        ...jsonHeader,
        authorization: user?.jwtToken ? `JWT ${user.jwtToken}` : undefined,
      };

      // Build redirect URI for OAuth callback
      const redirectUri = `${window.location.origin}/onboarding/oauth/${adPlatform}`;

      await oauthService.startOAuth({
        platform: adPlatform,
        apiType,
        accountId,
        data: {
          recaptcha: recaptchaResponse || 'logged-in-user',
          redirectUri,
          metadata: accountMetadata,
        },
        options: {
          method: 'POST',
          headers: headerWithAuth,
        },
      });
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const supportText = SiteName() === 'Limitless' ? (
    <p>For onboarding support, please contact <a href="mailto:adteam@limitless.tech">adteam@limitless.tech</a></p>
  ) : null;

  return (
    <Col md={12}>
      <Card>
        <Row>
          <Col md={6}>
            <CardBody>
              <div className="card__title">
                <h5 className="bold-text">Authorize {platformName} API access for {SiteName()}</h5>
              </div>

              {error && <Alert color="danger">{error}</Alert>}
              {warning && <Alert color="warning">{warning}</Alert>}

              {accountName && (
                <div className="form__form-group">
                  <p><strong>Account:</strong> {accountName}</p>
                </div>
              )}

              {!loggedIn && (
                <div className="form__form-group">
                  <Recaptcha
                    sitekey="6LdYQuAbAAAAAD-jRnBlWEzdtu7poJfrLQ72h_mZ"
                    onVerify={setRecaptchaResponse}
                  />
                </div>
              )}

              <LoadingSpinner active={loading} />

              {!loading && accountId && (
                <div className="form__form-group">
                  <button
                    type="button"
                    className="btn btn-primary account__btn"
                    onClick={handleAuthorize}
                    disabled={!loggedIn && !recaptchaResponse}
                  >
                    Authorize {platformName}
                  </button>
                </div>
              )}
            </CardBody>
          </Col>
          <Col md={6}>
            <CardBody>
              {platformConfig && platformConfig.requirements && (
                <div>
                  <p style={{ marginBottom: '8px' }}><strong>Requirements:</strong></p>
                  <ul style={{ marginLeft: 0, marginTop: 0 }}>
                    {platformConfig.requirements.map((req) => (
                      <li key={req}>{req}</li>
                    ))}
                  </ul>
                  <br />
                </div>
              )}
              <p>PLEASE NOTE:<br /><br /></p>
              <blockquote>
                <p>After you click &apos;Authorize&apos;, you will be directed to {platformName} to complete the authorization. When finished, {platformName} will redirect back to {SiteName()} where you will receive a success message.</p>
              </blockquote>
              <br />
              {supportText}
            </CardBody>
          </Col>
        </Row>
      </Card>
    </Col>
  );
};

PlatformAuthForm.propTypes = {
  adPlatform: PropTypes.string.isRequired,
  apiType: PropTypes.string,
  platformConfig: PropTypes.shape({
    displayName: PropTypes.string,
    description: PropTypes.string,
    requirements: PropTypes.arrayOf(PropTypes.string),
  }),
};

PlatformAuthForm.defaultProps = {
  apiType: 'ad-api',
  platformConfig: null,
};

export default PlatformAuthForm;
